import { t } from '@lingui/core/macro'
import { CellContext } from '@tanstack/react-table'
import ERC20 from 'abis/ERC20'
import humanizeDuration from 'humanize-duration'
import { durationLocale } from './locale'
import { BigNumberMap } from 'types'
import {
  Address,
  formatEther,
  formatUnits,
  getAddress,
  parseEther,
  parseUnits,
} from 'viem'
import { CHAIN_TO_NETWORK, RESERVE_STORAGE, ROUTES } from './constants'
import dayjs from 'dayjs'

export const decimalPattern = /^[0-9]*[.]?[0-9]*$/i
export const numberPattern = /^\d+$/
export const addressPattern = /^0x[a-fA-F0-9]{40}$/

export function getAssetURI(key: string) {
  return `${RESERVE_STORAGE}/${key}`
}

export const cutDecimals = (value: string, min = 2, max = 9) => {
  // Extract potential postfix
  const lastChar = value[value.length - 1]
  const postfix = isNaN(Number(lastChar)) ? lastChar : ''
  const numberPart = postfix ? value.slice(0, -1) : value

  const [integer, decimals] = numberPart.split('.')
  if (!decimals) return value
  if (min === 0) return integer + (postfix || '')
  if (decimals.length <= min) return value

  // Find first non-zero digit
  const firstNonZeroIndex = decimals.split('').findIndex((d) => d !== '0')

  let result
  if (firstNonZeroIndex === -1) {
    // All zeros after decimal, return min digits
    result = `${integer}.${decimals.slice(0, min)}`
  } else if (firstNonZeroIndex >= min) {
    // For very small numbers (many leading zeros), keep up to the first non-zero + 2 digits
    result = `${integer}.${decimals.slice(0, Math.min(firstNonZeroIndex + 1, decimals.length))}`
  } else {
    // Normal case - return min digits
    result = `${integer}.${decimals.slice(0, min)}`
  }

  // Add back postfix if it existed
  return postfix ? result + postfix : result
}

// returns the checksummed address if the address is valid, otherwise returns false
export function isAddress(value: string) {
  try {
    return getAddress(value)
  } catch {
    return null
  }
}

export function getPrice([lowPrice, highPrice]: [bigint, bigint]) {
  return Number(formatEther((lowPrice + highPrice) / 2n))
}

export const getTokenRoute = (
  token: string,
  chainId: number,
  route: string = ROUTES.OVERVIEW
) => `/${CHAIN_TO_NETWORK[chainId]}/token/${token.toLowerCase()}/${route}`

export const getFolioRoute = (
  token: string,
  chainId: number,
  route: string = ROUTES.OVERVIEW
) => `/${CHAIN_TO_NETWORK[chainId]}/index-dtf/${token.toLowerCase()}/${route}`

// multiplier 200 -> 2
export const getSafeGasLimit = (gas: bigint, multiplier = 200n) =>
  (gas * multiplier) / 100n

export function getCurrentTime() {
  return Math.floor(new Date().getTime() / 1000)
}

export function getTokenReadCalls(address: string) {
  const call = { address: address as Address, abi: ERC20, args: [] }

  return [
    {
      ...call,
      functionName: 'name',
    },
    {
      ...call,
      functionName: 'symbol',
    },
    {
      ...call,
      functionName: 'decimals',
    },
  ]
}

export const isAmountValid = (value: bigint, max: bigint) =>
  value > 0n && value <= max

const timeUnits = {
  year: 24 * 60 * 60 * 365,
  month: (24 * 60 * 60 * 365) / 12,
  day: 24 * 60 * 60,
  hour: 60 * 60,
  minute: 60,
  second: 0,
}

// Compact relative time ("4h", "1d", "2mth"). `from`/`to` are unix seconds.
// Each unit token is a catalog message so locales can localize the abbreviation
// (and reorder the count if needed).
export const relativeTime = (from: number, to: number) => {
  let delta = Math.abs(to - from)

  if (delta < 0) {
    delta = timeUnits.minute
  }

  if (delta >= timeUnits.year) {
    const count = Math.floor(delta / timeUnits.year)
    return t`${count}y`
  } else if (delta >= timeUnits.month) {
    const count = Math.floor(delta / timeUnits.month)
    return t`${count}mth`
  } else if (delta >= timeUnits.day) {
    const count = Math.floor(delta / timeUnits.day)
    return t`${count}d`
  } else if (delta >= timeUnits.hour) {
    const count = Math.floor(delta / timeUnits.hour)
    return t`${count}h`
  } else if (delta >= timeUnits.minute) {
    const count = Math.floor(delta / timeUnits.minute)
    return t`${count}m`
  } else {
    const count = Math.floor(delta)
    return t`${count}s`
  }
}

export const dateToUnix = (str: string): number => {
  const date = new Date(str)
  const time = date.getTime() - 50000 - date.getTimezoneOffset() * 60000
  return Math.floor(time / 1000)
}

export const getTime = (seconds: number) => {
  const d = Math.floor(seconds / (3600 * 24))
  const h = Math.floor((seconds % (3600 * 24)) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)

  const dDisplay = d > 0 ? `${d}d` : ''
  const hDisplay = h > 0 ? h + `${h}h` : ''
  const mDisplay = m > 0 ? m + `${m}m` : ''
  const sDisplay = s > 0 ? s + `${s}s` : ''
  return dDisplay + hDisplay + mDisplay + sDisplay
}

export function shortenString(str: string) {
  return `${str.substring(0, 6)}...${str.substring(str.length - 4)}`
}

export function shortenStringN(str: string, chars = 5) {
  return `${str.substring(0, chars)}...${str.substring(str.length - chars)}`
}

export function hasAllowance(
  allowances: BigNumberMap,
  requiredAllowances: BigNumberMap
) {
  return Object.keys(requiredAllowances).every(
    (address) => (allowances[address] || 0n) >= requiredAllowances[address]
  )
}

// Prevents more than 18 decimals
export function safeParseEther(value: string, decimals = 18): bigint {
  let safeValue = ''

  if (value[0] === '.') {
    safeValue = `0.${value.substring(1, decimals + 1) || 0}`
  } else {
    const split = value.split('.')
    safeValue = `${split[0]}.${split[1]?.substring(0, decimals) ?? '0'}`
  }

  return parseUnits(safeValue, decimals)
}

export function formatCurrency(
  value: number,
  decimals = 2,
  options: Intl.NumberFormatOptions = {}
): string {
  return Intl.NumberFormat('en-US', {
    maximumFractionDigits: decimals,
    minimumFractionDigits: Math.min(2, decimals),
    ...options,
  }).format(value)
}

export function formatCurrencyCompact(value: number, decimals = 2) {
  return formatCurrency(value, decimals, {
    notation: 'compact',
    compactDisplay: 'short',
  })
}

export function formatTokenAmount(value: number) {
  return value < 1
    ? formatCurrency(value, 0, {
        maximumSignificantDigits: 4,
        notation: 'compact',
        compactDisplay: 'short',
      })
    : formatCurrency(value, 2, {
        minimumFractionDigits: 0,
        notation: 'compact',
        compactDisplay: 'short',
      })
}

export function formatToSignificantDigits(
  value: number,
  digits = 4,
  options: Intl.NumberFormatOptions = {}
): string {
  return value >= 1
    ? Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        ...options,
      }).format(value)
    : Intl.NumberFormat('en-US', {
        maximumSignificantDigits: digits,
        ...options,
      }).format(value)
}

export const formatUSD = (value: number): string => {
  if (value > 0 && value < 0.01) return '< $0.01'
  return `$${formatCurrency(value)}`
}

export const formatPercentage = (value: number, decimals = 2): string =>
  (value / 100).toLocaleString('en-US', {
    style: 'percent',
    maximumFractionDigits: decimals,
  })

// Utils for rable parsing
export const formatCurrencyCell = (data: CellContext<any, number>) =>
  formatCurrency(+data.getValue())

export const formatUsdCurrencyCell = (data: CellContext<any, number>) =>
  `$${formatCurrency(+data.getValue())}`

// shorten the checksummed version of the input address to have 0x + 4 characters at start and end
export function shortenAddress(address: string, chars = 4): string {
  const parsed = isAddress(address)
  if (!parsed) {
    return shortenString(address)
  }
  return `${parsed.substring(0, chars + 2)}...${parsed.substring(42 - chars)}`
}

export function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

/**
 * Returns true if the string value is zero in hex
 * @param hexNumberString
 */
export function isZero(hexNumberString: string) {
  return /^0x0*$/.test(hexNumberString)
}

export const truncateDecimals = (number: number, digits = 3) => {
  const multiplier = Math.pow(10, digits),
    adjustedNum = number * multiplier,
    truncatedNum = Math[adjustedNum < 0 ? 'ceil' : 'floor'](adjustedNum)

  return truncatedNum / multiplier
}

export const stringToColor = (str: string) => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  let color = '#'
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff
    color += `00${value.toString(16)}`.substr(-2)
  }
  return color
}

export const parsePercent = (n: string): bigint => {
  return parseEther((Number(n) / 100).toString())
}

// Normalizes literal \n sequences to real newlines
export const normalizeDescription = (description: string) =>
  description.replace(/\\n/g, '\n')

export const getProposalTitle = (description: string) => {
  return normalizeDescription(description)
    .split(/\r?\n/)[0]
    .replaceAll('#', '')
    .trim()
}

const shortEnglishHumanizer = humanizeDuration.humanizer({
  language: 'shortEn',
  languages: {
    shortEn: {
      y: () => 'y',
      mo: () => 'mo',
      w: () => 'w',
      d: () => 'd',
      h: () => 'h',
      m: () => 'm',
      s: () => 's',
      ms: () => 'ms',
    },
  },
})

export const parseDuration = (
  duration: number,
  options?: humanizeDuration.Options
) => {
  return humanizeDuration(duration * 1000, {
    language: durationLocale(),
    ...options,
  })
}

export const parseDurationShort = (
  duration: number,
  options?: humanizeDuration.Options
) => {
  return shortEnglishHumanizer(duration * 1000, options)
}

export const getUTCStartOfDay = (timestamp: number) => {
  const date = new Date(timestamp * 1000)
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

// Thu May 18, 03:16 pm
export const formatDate = (timestamp?: string | number) => {
  const date = dayjs(timestamp)
  const currentYear = dayjs().year()
  const formatString =
    date.year() === currentYear
      ? 'ddd MMM DD, hh:mm a'
      : 'ddd MMM DD, YYYY, hh:mm a'
  return date.format(formatString)
}

export const humanizeDateToNow = (timestamp?: string | number) => {
  if (timestamp === undefined || timestamp === null) return ''

  let ts = Number(timestamp)
  if (Number.isNaN(ts)) return ''

  if (ts < 1e12) ts = ts * 1000

  const date = dayjs(ts)
  const diffMs = Math.abs(dayjs().diff(date))

  if (diffMs < 45 * 1000) return t`just now`

  const humanized = humanizeDuration(diffMs, {
    largest: 1,
    round: true,
    language: durationLocale(),
  })

  return t`${humanized} ago`
}

export const humanizeMinutes = (minutes: number) => {
  if (minutes <= 60) {
    return `${minutes}m`
  }
  return humanizeDuration(minutes * 60 * 1000, {
    language: durationLocale(),
  })
}

export const humanizeTimeFromHours = (hours: number) => {
  return humanizeDuration(hours * 60 * 60 * 1000, {
    language: durationLocale(),
  })
}

export const humanizeTimeFromDays = (days: number) => {
  return humanizeDuration(days * 24 * 60 * 60 * 1000, {
    language: durationLocale(),
  })
}

export const getTokenName = (name: string, stripOndoTokenized = true) => {
  if (name.startsWith('Moo ')) {
    return name.replace('Moo ', 'Beefy ')
  }

  return stripOndoTokenized ? name.replace(' (Ondo Tokenized)', '') : name
}

export const getFileNameFromUrl = (url: string) => {
  try {
    return decodeURIComponent(new URL(url).pathname.split('/').pop() || url)
  } catch {
    return url
  }
}

export const max = (...args: bigint[]): bigint => {
  if (args.length === 0) {
    throw new Error('empty arguments')
  }

  return args.reduce((max, curr) => (curr > max ? curr : max), 0n)
}

export function getTimerFormat(seconds: number) {
  const timeUnits = {
    days: 'd',
    day: 'd',
    hours: 'h',
    hour: 'h',
    minutes: 'm',
    minute: 'm',
    seconds: 's',
    second: 's',
  }

  let str = humanizeDuration(seconds * 1000, {
    units: ['h', 'm', 's'],
    round: true,
    spacer: '',
    delimiter: ' ',
  })

  // Replace all time unit words with their shortened versions
  for (const [word, short] of Object.entries(timeUnits)) {
    str = str.replace(word, short)
  }

  // Ensure seconds are always shown
  if (!str.includes('s')) {
    str += ' 0s'
  }

  return str
}

export const uuidv4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c == 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

// Check if the current version is greater than or equal to the required version
export const checkVersion = (
  requiredVersion: string,
  currentVersion: string
): boolean => {
  const requiredVersionParts = requiredVersion.split('.').map(Number)
  const currentVersionParts = currentVersion.split('.').map(Number)

  for (
    let i = 0;
    i < Math.max(requiredVersionParts.length, currentVersionParts.length);
    i++
  ) {
    const required = requiredVersionParts[i] || 0
    const current = currentVersionParts[i] || 0

    if (required > current) {
      return false
    }
    if (current > required) {
      return true
    }
  }

  return true
}

/**
 * Calculate the average price from a price range using geometric mean
 * @param priceRange - Object with low and high BigInt nanoUSD prices in D27 format
 * @param tokenDecimals - Number of decimals for the token
 * @returns Price per whole token in decimal format
 */
export function calculatePriceFromRange(
  priceRange: { low: bigint; high: bigint },
  tokenDecimals: number
): number {
  // Convert nanoUSD D27 BigInt prices to decimal numbers with proper precision
  const lowPrice = Number(formatUnits(priceRange.low, 36))
  const highPrice = Number(formatUnits(priceRange.high, 36))

  // Calculate average price as geometric mean (already in USD/tok format)
  const avgPricePerToken = Math.sqrt(lowPrice * highPrice)

  // Convert to whole token price (USD/wholeTok)
  return avgPricePerToken * Math.pow(10, tokenDecimals)
}

/**
 * Converts scientific notation numbers to standard decimal format
 * @param value - Number or string in scientific notation (e.g., "3.55e-8")
 * @returns String representation in standard decimal format (e.g., "0.0000000355")
 */
export function formatScientificNotation(value: number | string): string {
  const str = String(value)
  if (!/e/i.test(str)) return str

  const [mantissa, expStr] = str.toLowerCase().split('e')
  const exponent = parseInt(expStr, 10)

  const sign = mantissa.startsWith('-') ? '-' : ''
  const [intPart, fracPart = ''] = mantissa.replace(/^[+-]/, '').split('.')
  const digits = intPart + fracPart

  if (exponent >= 0) {
    // Positive exponent: move decimal point to the right
    if (exponent >= fracPart.length) {
      return sign + digits + '0'.repeat(exponent - fracPart.length)
    }
    return (
      sign +
      digits.slice(0, intPart.length + exponent) +
      '.' +
      digits.slice(intPart.length + exponent)
    )
  } else {
    // Negative exponent: move decimal point to the left
    const zeros = '0'.repeat(-exponent - intPart.length)
    if (-exponent >= intPart.length) {
      return sign + '0.' + zeros + digits
    }
    const cut = intPart.length + exponent
    return sign + intPart.slice(0, cut) + '.' + intPart.slice(cut) + fracPart
  }
}

/**
 * Format market cap value for display
 * @param marketCap - Market cap value in USD
 * @returns Formatted string (e.g., "1.2T", "456.7B", "89.1M")
 */
export const formatMarketCap = (marketCap: number): string => {
  if (!marketCap || marketCap === 0) return '—'

  if (marketCap >= 1e12) {
    return `$${(marketCap / 1e12).toFixed(1)}T`
  } else if (marketCap >= 1e9) {
    return `$${(marketCap / 1e9).toFixed(1)}B`
  } else if (marketCap >= 1e6) {
    return `$${(marketCap / 1e6).toFixed(1)}M`
  } else {
    return `$${(marketCap / 1e3).toFixed(1)}K`
  }
}
